/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import axios, { AxiosResponse } from 'axios'
import { createHash, randomBytes } from 'crypto'
import { encode } from 'urlsafe-base64'
import { LoginData } from './interfaces/LoginData'

const base64URLEncode = (buf: Buffer) => encode(buf)
const generateChallenge = (verifier: string) => createHash('sha256').update(verifier).digest()

export const generatePKCEValues = () => {
  const randomVal = randomBytes(32)
  const verifier = base64URLEncode(randomVal)
  const challenge = base64URLEncode(generateChallenge(verifier))
  return {
    verifier: verifier,
    challenge: challenge,
  }
}

export const generateAuthorizationParams = (
  pkceChallenge: string,
  publicClientId: string,
  redirectUri: string,
  scope: string
) => {
  return `?scope=${scope}&response_type=code&client_id=
            ${publicClientId}
            &redirect_uri=
            ${redirectUri}
            &code_challenge=
            ${pkceChallenge}
            &code_challenge_method=S256`.replace(/\s/g, '')
}

export const postLoginSession = (
  postUrl: string,
  data: string,
  baseUrl: string,
  publicClientId: string,
  redirectUri: string,
  revokeUrl: string
) => {
  const options = {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
  }

  return new Promise<LoginData>((resolve, reject) => {
    axios
      .post(postUrl, data, options)
      .then((response: AxiosResponse) => {
        const loginData = {
          ...response.data,
          baseUrl,
          publicClientId,
          redirectUri,
          revokeUrl,
        } as LoginData
        resolve(loginData)
      })
      .catch(error => {
        reject(error)
      })
  })
}

export const getAuthorizationData = (url: string, redirectUri: string, access_token?: string) => {
  if (access_token) {
    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        authorization: `Bearer ${access_token}`,
      },
      url,
    }

    return new Promise<any>((resolve, reject) => {
      axios(options)
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error)
        })
    })
  } else {
    location.href = redirectUri
  }
}

export const fetchUserData = (url: string, redirectUri: string, access_token?: string) => {
  if (access_token) {
    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        authorization: `Bearer ${access_token}`,
      },
      url,
    }

    return new Promise<any>((resolve, reject) => {
      axios(options)
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error)
        })
    })
  } else {
    location.href = redirectUri
  }
}

export const postRevokeToken = (publicClientID: string, postUrl: string, accessToken: string) => {
  const options = {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
  }

  let data = ''
  data += `client_id=${publicClientID}&`
  data += `token=${accessToken}&`
  data += `grant_type=access_token`

  return new Promise<any>((resolve, reject) => {
    axios
      .post(postUrl, data, options)
      .then((response: AxiosResponse) => {
        const loginData = {
          ...response.data,
        }
        resolve(loginData)
      })
      .catch(error => {
        reject(error)
      })
  })
}

export const postRefreshSession = (
  postUrl: string,
  publicClientID: string,
  refreshToken: string
) => {
  const options = {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
  }

  let data = ''
  data += `client_id=${publicClientID}&`
  data += `refresh_token=${refreshToken}&`
  data += `grant_type=refresh_token`

  return new Promise<LoginData>((resolve, reject) => {
    axios
      .post(postUrl, data, options)
      .then((response: AxiosResponse) => {
        const loginData = {
          ...response.data,
        } as LoginData
        resolve(loginData)
      })
      .catch(error => {
        reject(error)
      })
  })
}

export const clearSession = (loginData?: any, noRedirect?: boolean) => {
  if (loginData) {
    axios({
      method: 'post',
      url: `${loginData.revokeUrl}`,
      data: `client_id=${loginData.publicClientId}&token_type_hint=access_token&token=${loginData.access_token}`,
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
        Authorization: '',
      },
      withCredentials: true,
    })
      .then(() => {
        sessionStorage.clear()
        if (!noRedirect) {
          location.href = window.env.VITE_REVOKE_URL_REDIRECT
        }
      })
      .catch(() => {
        if (!noRedirect) {
          location.href = window.env.VITE_REVOKE_URL_REDIRECT
        }
      })
    // TODO: Currently, this redirects you to login screen and automatically logs you back in because of cookies
    // See if there is something additional that can be done here
  } else {
    if (!noRedirect) {
      location.href = loginData.redirectUri
    }
  }
}

export const getLoginDataProperty = (property: string | 'access_token' | 'id_token') => {
  const loginData = JSON.parse(sessionStorage.getItem('loginData') || 'null')

  if (loginData === null) return null

  return loginData[property]
}
